import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, BASE_PATH, type Role, type User } from '../api.ts';
import { can, useMe } from '../auth.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Table,
} from '../components/ui.tsx';
import { changed, FormModal, FormSection } from '../components/FormModal.tsx';
import {
  ListToolbar,
  SearchBox,
  StatusFilter,
  type StatusFilterValue,
} from '../components/ListFilters.tsx';
import { matchesUser, NO_ROLE, roleOptions } from './users-filters.ts';
import { ConfirmDialog, Modal } from '../components/Modal.tsx';
import { localized } from '../i18n/index.ts';

type Form = {
  email: string;
  displayName: string;
  locale: 'pt-BR' | 'en';
  timezone: string;
  isActive: boolean;
};
const EMPTY: Form = {
  email: '',
  displayName: '',
  locale: 'pt-BR',
  timezone: 'America/Sao_Paulo',
  isActive: true,
};

/** Edição do cadastro da pessoa, em janela por cima da lista (guia visual, FormModal). */
function UserForm({ user, onDone }: { user: User; onDone: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [initial] = useState<Form>(() => ({
    email: user.email,
    displayName: user.displayName,
    locale: user.locale,
    timezone: user.timezone,
    isActive: user.isActive,
  }));
  const [form, setForm] = useState<Form>(initial);
  const save = useMutation({
    mutationFn: () => api.put<User>(`/iam/users/${user.id}`, form),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['users'] });
      onDone();
    },
  });

  return (
    <FormModal
      open
      title={t('users.edit')}
      description={user.displayName}
      dirty={changed(initial, form)}
      saving={save.isPending}
      error={save.error}
      onSave={() => save.mutate()}
      onClose={onDone}
    >
      <FormSection>
        <Field label={t('users.email')} hint={t('users.emailChangeHint')}>
          <Input
            id="user-email"
            type="email"
            required
            value={form.email}
            onChange={(ev) => setForm({ ...form, email: ev.target.value })}
          />
        </Field>
        <Field label={t('fields.name')}>
          <Input
            id="user-name"
            required
            value={form.displayName}
            onChange={(ev) => setForm({ ...form, displayName: ev.target.value })}
          />
        </Field>
        <Field label={t('fields.language')}>
          <Select
            id="user-locale"
            value={form.locale}
            onChange={(ev) => setForm({ ...form, locale: ev.target.value as Form['locale'] })}
          >
            <option value="pt-BR">Português</option>
            <option value="en">English</option>
          </Select>
        </Field>
        <Field label={t('fields.timezone')} hint={t('fields.timezoneHint')}>
          <Input
            id="user-timezone"
            required
            value={form.timezone}
            onChange={(ev) => setForm({ ...form, timezone: ev.target.value })}
          />
        </Field>
        <label className="flex items-center gap-2 self-end text-sm">
          <input
            id="user-active"
            type="checkbox"
            checked={form.isActive}
            onChange={(ev) => setForm({ ...form, isActive: ev.target.checked })}
          />
          {t('fields.active')}
        </label>
        {!form.isActive && user.isActive && (
          <p className="text-sm text-danger sm:col-span-2">{t('users.deactivateWarning')}</p>
        )}
      </FormSection>
    </FormModal>
  );
}

/**
 * Põe a pessoa num grupo (perfil de acesso). Por ora vale sempre para o grupo todo: a estrutura
 * corporativa (empresas e filiais) está fora do menu.
 */
function AssignForm({ user, onDone }: { user: User; onDone: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.get<Role[]>('/iam/roles') });
  const [initial] = useState({ roleId: '' });
  const [form, setForm] = useState(initial);
  const taken = new Set(user.assignments.map((a) => a.roleId));
  const activeRoles = roles.data?.filter((r) => r.isActive && !taken.has(r.id)) ?? [];

  const save = useMutation({
    mutationFn: () => assignGroup(user.id, form.roleId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['users'] });
      onDone();
    },
  });

  return (
    <FormModal
      open
      size="md"
      title={t('users.assignTitle', { name: user.displayName })}
      description={t('users.assignNote')}
      saveLabel={t('users.assign')}
      dirty={changed(initial, form)}
      saving={save.isPending}
      error={save.error ?? (roles.isError ? roles.error : undefined)}
      onSave={() => save.mutate()}
      onClose={onDone}
    >
      <FormSection>
        {roles.isSuccess && activeRoles.length === 0 && (
          <p className="text-sm text-muted sm:col-span-2">{t('users.assignNone')}</p>
        )}
        <Field label={t('users.role')}>
          <Select
            id="assign-role"
            required
            value={form.roleId}
            onChange={(ev) => setForm({ roleId: ev.target.value })}
          >
            <option value="" disabled>
              {t('common.choose')}
            </option>
            {activeRoles.map((r) => (
              <option key={r.id} value={r.id}>
                {localized(r.name)}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>
    </FormModal>
  );
}

function assignGroup(userId: string, roleId: string) {
  return api.post('/iam/role-assignments', { userId, roleId });
}

interface InviteResult {
  /** Senha provisória (só nesta resposta): a pessoa troca no primeiro acesso (ADR 0109). */
  password: string;
  /** A pessoa já tinha senha: a antiga deixou de valer. */
  replaced: boolean;
}

type Invited =
  | { name: string; email: string; result: InviteResult }
  | { name: string; email: string; error: unknown };

/** Convite: cadastra a pessoa, põe nos grupos escolhidos e gera a senha provisória dela. */
function InviteForm({ onDone }: { onDone: (invited?: Invited) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const canGrant = can(me, 'iam.access.grant');
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<Role[]>('/iam/roles'),
    enabled: canGrant,
  });
  const [initial] = useState({
    email: '',
    displayName: '',
    locale: 'pt-BR' as Form['locale'],
    roleIds: [] as string[],
  });
  const [form, setForm] = useState(initial);
  const save = useMutation({
    mutationFn: async () => {
      const { roleIds, ...person } = form;
      const created = await api.post<User>('/iam/users', { ...EMPTY, ...person });
      for (const roleId of roleIds) await assignGroup(created.id, roleId);
      return created;
    },
    onSuccess: async (created) => {
      await qc.invalidateQueries({ queryKey: ['users'] });
      // O cadastro já existe: se o convite falhar, ele é gerado de novo pela lista.
      try {
        const result = await api.post<InviteResult>(`/iam/users/${created.id}/invite`);
        onDone({ name: created.displayName, email: created.email, result });
      } catch (error) {
        onDone({ name: created.displayName, email: created.email, error });
      }
    },
  });
  const toggle = (id: string) =>
    setForm({
      ...form,
      roleIds: form.roleIds.includes(id)
        ? form.roleIds.filter((r) => r !== id)
        : [...form.roleIds, id],
    });

  return (
    <FormModal
      open
      title={t('users.inviteTitle')}
      description={t('users.inviteNote')}
      saveLabel={t('users.inviteSend')}
      dirty={changed(initial, form)}
      saving={save.isPending}
      error={save.error ?? (roles.isError ? roles.error : undefined)}
      onSave={() => save.mutate()}
      onClose={() => onDone()}
    >
      <FormSection>
        <Field label={t('users.email')}>
          <Input
            id="invite-email"
            type="email"
            required
            value={form.email}
            onChange={(ev) => setForm({ ...form, email: ev.target.value })}
          />
        </Field>
        <Field label={t('fields.name')}>
          <Input
            id="invite-name"
            required
            value={form.displayName}
            onChange={(ev) => setForm({ ...form, displayName: ev.target.value })}
          />
        </Field>
        <Field label={t('fields.language')}>
          <Select
            id="invite-locale"
            value={form.locale}
            onChange={(ev) => setForm({ ...form, locale: ev.target.value as Form['locale'] })}
          >
            <option value="pt-BR">Português</option>
            <option value="en">English</option>
          </Select>
        </Field>
        {canGrant && (
          <fieldset className="flex flex-col gap-2 sm:col-span-2">
            <legend className="mb-1 text-sm font-medium text-ink">{t('users.inviteGroups')}</legend>
            {(roles.data ?? [])
              .filter((r) => r.isActive)
              .map((r) => (
                <label key={r.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.roleIds.includes(r.id)}
                    onChange={() => toggle(r.id)}
                  />
                  {localized(r.name)}
                </label>
              ))}
            <p className="text-xs text-muted">{t('users.inviteGroupsHint')}</p>
          </fieldset>
        )}
      </FormSection>
    </FormModal>
  );
}

/** Mostra a senha provisória para quem convidou copiar e mandar à pessoa. */
function InviteLinkDialog({ invited, onClose }: { invited: Invited; onClose: () => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const result = 'result' in invited ? invited.result : null;
  // Texto pronto para mandar (WhatsApp, e-mail): endereço, usuário e senha provisória.
  const message = result
    ? [
        t('users.inviteMessageOpen', { url: `${window.location.origin}${BASE_PATH}/` }),
        t('users.inviteMessageUser', { email: invited.email }),
        t('users.inviteMessagePassword', { password: result.password }),
        t('users.inviteMessageChange'),
      ].join('\n')
    : '';
  return (
    <Modal
      open
      size="md"
      title={t('users.inviteLinkTitle', { name: invited.name })}
      onClose={onClose}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      {'error' in invited ? (
        <div className="flex flex-col gap-3 text-sm">
          <p>{t('users.invitePartial')}</p>
          <ErrorBox error={invited.error} />
        </div>
      ) : (
        <div className="flex flex-col gap-3 text-sm">
          <p>{t(result?.replaced ? 'users.inviteReplacedText' : 'users.inviteLinkText')}</p>
          <textarea
            id="invite-message"
            readOnly
            rows={5}
            value={message}
            onFocus={(ev) => ev.target.select()}
            className="w-full rounded-md border border-rule bg-paper p-2 text-sm text-ink"
          />
          <div>
            <Button
              variant="secondary"
              onClick={() =>
                void navigator.clipboard?.writeText(message).then(() => setCopied(true))
              }
            >
              {copied ? t('users.inviteCopied') : t('users.inviteCopy')}
            </Button>
          </div>
          <p className="text-xs text-muted">{t('users.inviteLinkHint')}</p>
        </div>
      )}
    </Modal>
  );
}

type UserDialog = { assignmentId: string; role: string; name: string };

export function UsersPage() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  // Ninguém concede nem retira os próprios perfis (o banco também barra).
  const selfLocked = (id: string) => id === me?.user.id;
  const qc = useQueryClient();
  const canManage = can(me, 'iam.user.manage');
  const canGrant = can(me, 'iam.access.grant');
  const [open, setOpen] = useState<
    { kind: 'user'; user: User } | { kind: 'invite' } | { kind: 'assign'; user: User } | null
  >(null);
  const [invited, setInvited] = useState<Invited | null>(null);
  // Nova senha provisória: para quem perdeu a primeira ou esqueceu a senha. Quem já tem senha
  // passa por uma confirmação, porque a senha atual deixa de valer.
  const reinvite = useMutation({
    mutationFn: (u: User) => api.post<InviteResult>(`/iam/users/${u.id}/invite`),
    onSuccess: (result, u) => {
      setResetting(null);
      setInvited({ name: u.displayName, email: u.email, result });
    },
  });
  const [resetting, setResetting] = useState<User | null>(null);

  // Confirmações abrem em janela (guia visual, Modal), nunca no navegador.
  const [dialog, setDialog] = useState<UserDialog | null>(null);

  const list = useQuery({ queryKey: ['users'], queryFn: () => api.get<User[]>('/iam/users') });
  // Busca e filtros (guia visual): nome ou e-mail, situação e perfil.
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilterValue>('active');
  const [roleFilter, setRoleFilter] = useState('');
  const roles = roleOptions(list.data ?? [], localized, i18n.language);
  const rows = (list.data ?? []).filter((u) => matchesUser(u, { q, status, roleId: roleFilter }));
  // Exclusão: a pessoa some da lista e perde o acesso na hora (o histórico fica).
  const [toDelete, setToDelete] = useState<User | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/iam/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
  const unassign = useMutation({
    mutationFn: (id: string) => api.delete(`/iam/role-assignments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
  const dateTime = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'short',
  });

  return (
    <>
      <PageHeader
        title={t('users.title')}
        description={t('users.description')}
        actions={
          canManage ? (
            <Button onClick={() => setOpen({ kind: 'invite' })}>{t('users.invite')}</Button>
          ) : null
        }
      />

      {open?.kind === 'user' && (
        <UserForm key={open.user.id} user={open.user} onDone={() => setOpen(null)} />
      )}
      {open?.kind === 'invite' && (
        <InviteForm
          onDone={(result) => {
            setOpen(null);
            if (result) setInvited(result);
          }}
        />
      )}
      {invited && <InviteLinkDialog invited={invited} onClose={() => setInvited(null)} />}
      <ConfirmDialog
        open={resetting !== null}
        title={t('users.newPasswordTitle', { name: resetting?.displayName ?? '' })}
        description={t('users.newPasswordText')}
        confirmLabel={t('users.newPassword')}
        busy={reinvite.isPending}
        error={reinvite.error}
        onConfirm={() => resetting && reinvite.mutate(resetting)}
        onCancel={() => setResetting(null)}
      />
      <ErrorBox error={reinvite.error} />
      {open?.kind === 'assign' && (
        <AssignForm key={open.user.id} user={open.user} onDone={() => setOpen(null)} />
      )}
      <ErrorBox error={unassign.error} />
      <ErrorBox error={remove.error} />
      <ConfirmDialog
        open={toDelete !== null}
        title={t('users.delete')}
        description={
          toDelete ? t('users.deleteConfirm', { name: toDelete.displayName }) : undefined
        }
        confirmLabel={t('users.delete')}
        tone="danger"
        busy={remove.isPending}
        onCancel={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete) remove.mutate(toDelete.id);
          setToDelete(null);
        }}
      />
      <ConfirmDialog
        open={dialog !== null}
        title={t('users.removeRole')}
        description={
          dialog
            ? t('users.removeRoleConfirm', { role: dialog.role, name: dialog.name })
            : undefined
        }
        confirmLabel={t('users.removeRole')}
        tone="danger"
        busy={unassign.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={() => {
          if (dialog) unassign.mutate(dialog.assignmentId);
          setDialog(null);
        }}
      />
      <ListToolbar>
        <SearchBox value={q} onChange={setQ} placeholder={t('users.search')} />
        <StatusFilter value={status} onChange={setStatus} />
        <Select
          id="users-role-filter"
          className="w-full sm:w-64"
          aria-label={t('users.filterByRole')}
          value={roleFilter}
          onChange={(ev) => setRoleFilter(ev.target.value)}
        >
          <option value="">{t('users.allRoles')}</option>
          <option value={NO_ROLE}>{t('users.withoutRole')}</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </Select>
      </ListToolbar>

      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorBox error={list.error} />
      ) : (
        <Table
          head={[t('fields.name'), t('users.roles'), t('users.lastLogin'), t('fields.status'), '']}
        >
          {rows.length === 0 && (
            <EmptyRow colSpan={5}>
              {list.data.length === 0 ? t('users.empty') : t('users.noMatch')}
            </EmptyRow>
          )}
          {rows.map((u) => (
            <tr key={u.id}>
              <td>
                {u.displayName}
                <div className="break-all text-xs text-muted">{u.email}</div>
              </td>
              <td>
                {u.assignments.length === 0 && (
                  <span className="text-xs text-muted">{t('users.noRoles')}</span>
                )}
                <ul className="flex flex-col gap-1">
                  {u.assignments.map((a) => (
                    <li key={a.id} className="flex flex-wrap items-center gap-2 text-xs">
                      <Badge tone="accent">{localized(a.roleName)}</Badge>
                      {canGrant && !selfLocked(u.id) && (
                        <button
                          type="button"
                          className="text-danger hover:underline disabled:opacity-50"
                          disabled={unassign.isPending}
                          aria-label={t('users.removeRole')}
                          onClick={() =>
                            setDialog({
                              assignmentId: a.id,
                              role: localized(a.roleName),
                              name: u.displayName,
                            })
                          }
                        >
                          {t('users.removeRole')}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </td>
              <td className="text-xs">
                {u.lastLoginAt
                  ? dateTime.format(new Date(u.lastLoginAt))
                  : t('users.neverLoggedIn')}
                {!u.linked && <div className="text-muted">{t('users.notLinked')}</div>}
              </td>
              <td>
                {u.isActive ? (
                  <Badge tone="ok">{t('fields.active')}</Badge>
                ) : (
                  <Badge tone="off">{t('fields.inactive')}</Badge>
                )}
              </td>
              <td className="text-right">
                <div className="flex justify-end gap-2">
                  {canManage && u.isActive && !selfLocked(u.id) && (
                    <Button
                      variant="secondary"
                      disabled={reinvite.isPending}
                      onClick={() => (u.linked ? setResetting(u) : reinvite.mutate(u))}
                    >
                      {u.linked ? t('users.newPassword') : t('users.inviteAgain')}
                    </Button>
                  )}
                  {canGrant && !selfLocked(u.id) && (
                    <Button
                      variant="secondary"
                      onClick={() => setOpen({ kind: 'assign', user: u })}
                    >
                      {t('users.assign')}
                    </Button>
                  )}
                  {canManage && (
                    <Button variant="secondary" onClick={() => setOpen({ kind: 'user', user: u })}>
                      {t('common.edit')}
                    </Button>
                  )}
                  {canManage && !selfLocked(u.id) && (
                    <Button
                      variant="danger"
                      disabled={remove.isPending}
                      onClick={() => setToDelete(u)}
                    >
                      {t('users.delete')}
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}
